import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBillingStore } from '@/stores/useBillingStore';
import { UPGRADE_MODAL_COPY } from '@/features/billing/constants/upgradeModalCopy';

const getBaseUrl = (): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const customUrl = import.meta.env.VITE_API_URL as string | undefined;
    if (customUrl && customUrl !== '/api') {
      return customUrl.replace(/\/+$/, '');
    }
  }
  return '';
};

export const AXIOS_INSTANCE = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const apiClient = AXIOS_INSTANCE;

// Request interceptor: attach Bearer token
AXIOS_INSTANCE.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    let token: string | null = null;
    try {
      const authState = useAuthStore.getState();
      if (authState.isAuthenticated && authState.token) {
        token = authState.token;
      }
    } catch {
      // Store not initialized yet or in non-React environment
    }

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    } else if (config.headers) {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

import { toast } from 'sonner';

// Response interceptor: handle 401, 403, and business errors
AXIOS_INSTANCE.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<{ code?: string; message?: string }>) => {
    const status = error.response?.status;
    const data = error.response?.data;
    
    const isQuotaExceeded = () => {
      if (status === 402) return true;
      const errorCode = (data as any)?.error || (data as any)?.code || (data as any)?.errorCode || (data as any)?.type;
      const desc = (data as any)?.description || (data as any)?.message || (data as any)?.detail || (data as any)?.title;
      const payloadStr = JSON.stringify(data || '').toLowerCase();
      return (
        errorCode === 'Workspace.TeamQuotaExceeded' ||
        errorCode === 'TeamQuotaExceeded' ||
        payloadStr.includes('teamquotaexceeded') ||
        payloadStr.includes('quota') ||
        (typeof desc === 'string' && /quota|limit|plano|upgrade/i.test(desc))
      );
    };

    if (status === 401) {
      try {
        useAuthStore.getState().logout();
      } catch {
        // ignore
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('chatagent_auth');
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('unauthorized'));
        window.dispatchEvent(new CustomEvent('unauthorized'));
      }
    } else if (isQuotaExceeded()) {
      const errorCode = (data as any)?.error || (data as any)?.code || (data as any)?.errorCode || (data as any)?.type;
      const isTeamQuota =
        errorCode === 'Workspace.TeamQuotaExceeded' ||
        errorCode === 'TeamQuotaExceeded' ||
        JSON.stringify(data || '').toLowerCase().includes('teamquota');

      const backendDescription = (data as any)?.description || (data as any)?.message || (data as any)?.detail;

      try {
        useBillingStore.getState().openUpgradeModal({
          title: isTeamQuota
            ? UPGRADE_MODAL_COPY.team_quota.title
            : UPGRADE_MODAL_COPY.usage_quota.title,
          description:
            backendDescription ||
            (isTeamQuota
              ? UPGRADE_MODAL_COPY.team_quota.description
              : UPGRADE_MODAL_COPY.usage_quota.description),
        });
      } catch {
        // Safeguard for non-React or test environments
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('team_quota_exceeded', { detail: data }));
      }
    } else if (status === 403) {
      const code = (data as any)?.error || (data as any)?.code || (data as any)?.errorCode;
      const desc = (data as any)?.description || (data as any)?.message || (data as any)?.detail;

      if (code === 'Workspace.InvitationEmailMismatch' || code === 'InvitationEmailMismatch') {
        toast.error('Este convite foi enviado para outro e-mail. Conecte-se com a conta correta.');
      } else {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('forbidden_access'));
        }
        toast.error(desc || 'Você não tem permissão para realizar esta ação.');
      }
    } else if (status === 400 || status === 409 || status === 422) {
      const code = (data as any)?.error || (data as any)?.code || (data as any)?.errorCode;
      const desc = (data as any)?.description || (data as any)?.message || (data as any)?.detail;

      if (code === 'Workspace.UserAlreadyMember' || code === 'UserAlreadyMember') {
        toast.info('Você já é membro deste workspace.');
      } else if (code === 'Workspace.CannotDeleteLastWorkspace' || code === 'CannotDeleteLastWorkspace') {
        toast.error('Você não pode excluir seu único workspace. Crie outro primeiro.');
      } else if (code === 'Workspace.ActiveSubscriptionPreventsDeletion' || code === 'ActiveSubscriptionPreventsDeletion') {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('active_subscription_prevents_deletion', { detail: data }));
        }
        toast.error('Cancele sua assinatura ativa no portal de faturamento antes de excluir o workspace.');
      } else if (code === 'Workspace.NameConfirmationMismatch' || code === 'NameConfirmationMismatch') {
        toast.error('O nome digitado não confere com o nome do workspace.');
      } else if (code === 'CannotInviteOwner') {
        toast.error(desc || 'Não é possível convidar um usuário diretamente como Dono.');
      } else if (code === 'AdminCanOnlyRemoveAgents') {
        toast.error(desc || 'Administradores só podem remover Agentes.');
      } else if (code === 'CannotDemoteLastOwner' || code === 'Workspace.CannotDemoteLastOwner') {
        toast.error(desc || 'Não é possível alterar a função do único Dono do workspace.');
      } else if (code === 'CannotRemoveLastOwner' || code === 'Workspace.CannotRemoveLastOwner') {
        toast.error(desc || 'Não é possível remover o único Dono do workspace.');
      } else if (code === 'PasswordRequired') {
        toast.error(desc || 'Uma senha é obrigatória para concluir o cadastro.');
      } else if (desc) {
        toast.error(desc);
      }
    }

    return Promise.reject(error);
  }
);

// Custom instance mutator for Orval
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // @ts-expect-error cancel token augmentation
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

export const customAxiosInstance = customInstance;
export default customInstance;
